import type { CustomerObjectEntry, CustomerRow } from "../_shared/_logic/customers-table-contract"
import {
  buildContactPointsByCustomerId,
  buildContactTags,
  contactKindLabel,
  customerTypeColor,
  customerTypeIcon,
  customerTypeLabel,
  formatDate,
  truncateTagLabel,
} from "../_shared/_logic/contact-presentation"
import {
  buildFallbackObjectEntries,
  buildObjectEntries,
  roleValuesForLinks,
} from "../_shared/_logic/object-presentation"

export const buildCustomerRows = ({
  customers,
  propertyLinks,
  properties,
  contactPoints,
}: {
  customers: CustomerListEntry[]
  propertyLinks: PropertyCustomer[]
  properties: Property[]
  contactPoints: ContactPoint[]
}): CustomerRow[] => {
  const propertiesById = Object.fromEntries(
    properties.map((property) => [property.id, property]),
  ) as Record<string, Property>
  const contactPointsByCustomerId = buildContactPointsByCustomerId(contactPoints)
  const linksByCustomerId = propertyLinks.reduce(
    (acc, link) => {
      const list = acc[link.customer_id] ?? []
      list.push(link)
      acc[link.customer_id] = list

      return acc
    },
    {} as Record<string, PropertyCustomer[]>,
  )

  return customers.map((customer) =>
    toCustomerRow({
      customer,
      objectEntries: buildObjectEntries(customer, linksByCustomerId, propertiesById),
      roleValues: roleValuesForLinks(linksByCustomerId[customer.id] ?? []),
      contactPoints: contactPointsByCustomerId[customer.id] ?? [],
    }),
  )
}

export const toCustomerRow = ({
  customer,
  objectEntries,
  roleValues = [],
  contactPoints = [],
}: {
  customer: CustomerListEntry
  objectEntries: CustomerObjectEntry[]
  roleValues?: PropertyCustomerRole[]
  contactPoints?: ContactPoint[]
}): CustomerRow => ({
  id: customer.id,
  identityLabel: truncateTagLabel(customer.display_name),
  identityTooltip: [customerTypeLabel(customer.type), customer.display_name]
    .filter(Boolean)
    .join("\n"),
  identityIcon: customerTypeIcon(customer.type),
  identityColor: customerTypeColor(customer.type),
  contactValue: (customer.primary_contact_value ?? "").trim() || null,
  contactKind: (customer.primary_contact_kind ?? "").trim() || null,
  contactTags: buildContactTags(contactPoints),
  contactTooltip: customer.primary_contact_value
    ? `${contactKindLabel(customer.primary_contact_kind)}: ${customer.primary_contact_value}`
    : "Keine Kontaktdaten hinterlegt",
  roleValues,
  objectEntries: objectEntries.length ? objectEntries : buildFallbackObjectEntries(customer),
  updatedLabel: formatDate(customer.updated_at),
  customer,
})
